import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Grid,
  TextField,
} from '@material-ui/core';
import { ChevronRight, Close as CloseIcon, ArrowBack } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { DatePicker } from '@material-ui/pickers';
import { unpackToDate, encodeDate } from '@campfire/hot-date';
import { DateTime } from 'luxon';
import { gql } from '@apollo/client';
import { useCampfireLazyQuery } from '../../../../global/network/useCampfireLazyQuery';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { SessionReport } from './SessionReport';

const GET_EXISTING_REPORT_DATES = gql`
  query GetExistingReportDates($activityId: String!, $from: Date, $until: Date) {
    vm {
      activityReports(activityId: $activityId, from: $from, until: $until) {
        activityDate
      }
    }
  }
`;

const GET_ACTIVITY_DATA = gql`
  query GetActivityData($activityId: String!, $activityDate: Date) {
    vm {
      allowCICO
      activity(activityId: $activityId) {
        activityId
        name
        allowCICO
        program {
          programId
          name
        }
        allSessions(includeOneOff: true, activityDate: $activityDate) {
          sessionId
          name
          description
          startTime
          endTime
          isFlexible
          autoReport
          dateRemoved
          activity {
            activityId
            name
            allowCICO
            program {
              programId
              name
            }
          }
          reportType {
            reportTypeId
            name
            items {
              ... on VOLUNTEER_ReportTypeFieldType {
                reportTypeItemId
                order
                optional
                field {
                  fieldId
                  name
                  description
                  isHero
                  ... on VOLUNTEER_DropdownFieldType {
                    isAllowMultiple
                    dropdownOptions {
                      dropdownFieldOptionId
                      name
                      order
                    }
                  }
                  ... on VOLUNTEER_TextFieldType {
                    isLongText
                  }
                }
              }
              ... on VOLUNTEER_ReportTypeHeadingType {
                reportTypeItemId
                order
                heading
              }
            }
          }
          currentFlexibleInstances {
            flexibleSessionInstanceId
            sessionDate
            startTime
            endTime
            name
          }
        }
        publishedRoster(activityDate: $activityDate) {
          rosterId
          datePublished
          rosterings {
            publishedRosteringId
            sessionRosterings {
              publishedSessionRosteringId
              session {
                sessionId
              }
            }
            volunteer {
              volunteerId
              profile {
                profileId
                preferredName
                lastName
                avatarUrl
              }
            }
          }
          activityDate
        }
      }
    }
  }
`;

interface EligibleActivity {
  activityId: string;
  activityName: string;
  programId: string;
  programName: string;
}

interface AdHocReportDialogProps {
  open: boolean;
  onClose: () => void;
  eligibleActivities: EligibleActivity[];
  onSuccess: () => void;
}

enum DialogStep {
  ActivitySelection = 'ActivitySelection',
  DateSelection = 'DateSelection',
  SessionSelection = 'SessionSelection',
}

export function AdHocReportDialog({ open, onClose, eligibleActivities, onSuccess }: AdHocReportDialogProps) {
  const [currentStep, setCurrentStep] = useState<DialogStep>(DialogStep.ActivitySelection);
  const [selectedActivity, setSelectedActivity] = useState<EligibleActivity | null>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [activityDate, setActivityDate] = useState<Date | null>(new Date());
  const [existingReportDates, setExistingReportDates] = useState<string[]>([]);
  const [orgAllowCICO, setOrgAllowCICO] = useState<boolean>(false);

  const { setSnackbar } = useSnackbar();

  const [getActivityData, { loading: loadingSessions }] = useCampfireLazyQuery(GET_ACTIVITY_DATA);
  const [getExistingReportDates] = useCampfireLazyQuery(GET_EXISTING_REPORT_DATES);

  // Fetch sessions when SessionSelection step is reached
  React.useEffect(() => {
    if (currentStep === DialogStep.SessionSelection && selectedActivity && activityDate && sessions.length === 0) {
      getActivityData({
        variables: {
          activityId: selectedActivity.activityId,
          activityDate: encodeDate(DateTime.fromJSDate(new Date(activityDate))),
        },
      })
        .then(({ data }) => {
          if (data?.vm?.activity?.allSessions) {
            setSessions(data.vm.activity.allSessions);
          }
          if (data?.vm?.allowCICO !== undefined) {
            setOrgAllowCICO(data.vm.allowCICO);
          }
        })
        .catch((error) => {
          console.error('Error fetching sessions:', error);
          setSnackbar({
            open: true,
            message: 'Error loading sessions for this date',
            variant: 'error',
          });
        });
    }
  }, [currentStep, selectedActivity, activityDate, sessions.length, getActivityData, setSnackbar]);

  // Fetch existing report dates when entering date selection step
  React.useEffect(() => {
    if (currentStep === DialogStep.DateSelection && selectedActivity && existingReportDates.length === 0) {
      const twelveMonthsAgo = new Date();
      twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
      const today = new Date();

      getExistingReportDates({
        variables: {
          activityId: selectedActivity.activityId,
          from: encodeDate(DateTime.fromJSDate(twelveMonthsAgo)),
          until: encodeDate(DateTime.fromJSDate(today)),
        },
      })
        .then(({ data }) => {
          if (data?.vm?.activityReports) {
            const dates = data.vm.activityReports.map((report: any) => report.activityDate);
            setExistingReportDates(dates);
          }
        })
        .catch((error) => {
          console.error('Error fetching existing report dates:', error);
          // Don't show error to user - just continue without date restrictions
        });
    }
  }, [currentStep, selectedActivity, existingReportDates.length, getExistingReportDates]);

  const handleClose = () => {
    setCurrentStep(DialogStep.ActivitySelection);
    setSelectedActivity(null);
    setSessions([]);
    setActivityDate(new Date());
    setExistingReportDates([]);
    onClose();
  };

  const handleActivitySelect = (activity: EligibleActivity) => {
    setSelectedActivity(activity);
    setCurrentStep(DialogStep.DateSelection);
  };

  const handleBack = () => {
    if (currentStep === DialogStep.SessionSelection) {
      setCurrentStep(DialogStep.DateSelection);
      setSessions([]); // Clear sessions when going back
    } else if (currentStep === DialogStep.DateSelection) {
      setCurrentStep(DialogStep.ActivitySelection);
      setSelectedActivity(null);
      setSessions([]); // Clear sessions when going back
      setExistingReportDates([]); // Clear existing report dates when going back
    }
  };

  const renderActivitySelection = () => (
    <>
      <DialogTitle>
        <Grid container justify='space-between' alignItems='center'>
          <Grid item>
            <Typography variant='h6' style={{ fontWeight: 600 }}>
              Select Activity
            </Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={handleClose} size='small'>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Box mb={3}>
          <Typography variant='body2' color='textSecondary' style={{ lineHeight: 1.6 }}>
            Choose an activity to create an ad-hoc report for. Ad-hoc reports allow you to submit session reports for
            past activities that you attended.
          </Typography>
        </Box>
        <List>
          {eligibleActivities.map((activity) => (
            <React.Fragment key={activity.activityId}>
              <ListItem
                button
                onClick={() => handleActivitySelect(activity)}
                style={{
                  paddingTop: 16,
                  paddingBottom: 16,
                  borderRadius: 8,
                  marginBottom: 8,
                  backgroundColor: '#f5f5f5',
                  '&:hover': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant='body1' style={{ fontWeight: 500 }}>
                      {activity.activityName}
                    </Typography>
                  }
                  secondary={
                    <Typography variant='body2' color='textSecondary'>
                      {activity.programName}
                    </Typography>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton edge='end' onClick={() => handleActivitySelect(activity)}>
                    <ChevronRight />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </DialogContent>
    </>
  );

  const renderDateSelection = () => {
    const handleDateContinue = () => {
      if (!activityDate || !selectedActivity) return;

      // Just move to the next step - we'll fetch data when the session selection renders
      setCurrentStep(DialogStep.SessionSelection);
    };

    return (
      <>
        <DialogTitle>
          <Grid container justify='space-between' alignItems='center'>
            <Grid item xs>
              <Box display='flex' alignItems='center'>
                <IconButton onClick={handleBack} size='small' style={{ marginRight: 12 }}>
                  <ArrowBack />
                </IconButton>
                <Box>
                  <Typography variant='h6' style={{ fontWeight: 600 }}>
                    Select Date
                  </Typography>
                  <Typography variant='body2' color='primary' style={{ marginTop: 2, fontWeight: 500 }}>
                    {selectedActivity?.activityName}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <IconButton onClick={handleClose} size='small'>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Box py={2}>
            <Typography variant='body2' color='textSecondary' style={{ lineHeight: 1.6, marginBottom: 32 }}>
              Choose the date when the activity occurred. You can submit reports for past activity sessions. Select the
              date to see available sessions.
            </Typography>

            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              style={{
                backgroundColor: '#f8f9fa',
                borderRadius: 12,
                padding: '40px 20px',
                marginBottom: 24,
              }}
            >
              <DatePicker
                label='Activity Date'
                value={activityDate}
                onChange={setActivityDate}
                maxDate={new Date()}
                minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 1))}
                shouldDisableDate={(date) => {
                  if (!date) return false;
                  const dateString = encodeDate(DateTime.fromJSDate(new Date(date)));
                  return existingReportDates.includes(dateString);
                }}
                renderInput={(props) => (
                  <TextField
                    {...props}
                    variant='outlined'
                    size='medium'
                    style={{
                      minWidth: 380,
                      backgroundColor: 'white',
                    }}
                    InputProps={{
                      ...props.InputProps,
                      style: {
                        fontSize: '1.1rem',
                        padding: '4px 8px',
                      },
                    }}
                    InputLabelProps={{
                      ...props.InputLabelProps,
                      style: {
                        fontSize: '1rem',
                      },
                    }}
                  />
                )}
                format='dd/MM/yyyy'
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: 16 }}>
          <TabletButton size='medium' variant='text' onClick={handleBack}>
            Back
          </TabletButton>
          <TabletButton
            size='medium'
            variant='contained'
            color='primary'
            onClick={handleDateContinue}
            disabled={!activityDate}
          >
            Continue
          </TabletButton>
        </DialogActions>
      </>
    );
  };

  const renderSessionSelection = () => {
    const activityDateString = activityDate ? encodeDate(DateTime.fromJSDate(new Date(activityDate))) : '';

    const handleRefresh = () => {
      // Refresh the sessions after a report is submitted
      if (selectedActivity && activityDate) {
        getActivityData({
          variables: {
            activityId: selectedActivity.activityId,
            activityDate: encodeDate(DateTime.fromJSDate(new Date(activityDate))),
          },
        }).then(({ data }) => {
          if (data?.vm?.activity?.allSessions) {
            setSessions(data.vm.activity.allSessions);
          }
        });
      }
      // Call the parent's onSuccess to refresh the main page
      onSuccess();
    };

    return (
      <>
        <DialogTitle>
          <Grid container justify='space-between' alignItems='center'>
            <Grid item xs>
              <Box display='flex' alignItems='center'>
                <IconButton onClick={handleBack} size='small' style={{ marginRight: 12 }}>
                  <ArrowBack />
                </IconButton>
                <Box>
                  <Typography variant='h6' style={{ fontWeight: 600 }}>
                    Session Reports
                  </Typography>
                  <Typography variant='body2' color='primary' style={{ marginTop: 2, fontWeight: 500 }}>
                    {selectedActivity?.activityName} -{' '}
                    {activityDateString && unpackToDate(activityDateString).toFormat('DD')}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <IconButton onClick={handleClose} size='small'>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Box pt={1} pb={2}>
            <Typography variant='body2' color='textSecondary' style={{ lineHeight: 1.6, marginBottom: 24 }}>
              Select a session to submit your report. Click on any session below to open the report form where you can
              fill in attendance, comments, and other details.
            </Typography>

            {loadingSessions ? (
              <Box display='flex' justifyContent='center' py={4}>
                <Typography variant='body2' color='textSecondary'>
                  Loading sessions...
                </Typography>
              </Box>
            ) : sessions.length === 0 ? (
              <Box
                display='flex'
                justifyContent='center'
                alignItems='center'
                style={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: 12,
                  padding: '40px 20px',
                  marginTop: 16,
                }}
              >
                <Typography color='textSecondary'>No sessions available for this date</Typography>
              </Box>
            ) : (
              <Box>
                {sessions.map((session: any) => (
                  <Box key={session.sessionId} mb={1}>
                    <SessionReport
                      variant='null'
                      session={session}
                      activityDate={activityDateString || ''}
                      activityName={selectedActivity?.activityName}
                      publishedRoster={null}
                      onEdit={handleRefresh}
                      isAdHoc
                      orgAllowCICO={orgAllowCICO}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
      </>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      {currentStep === DialogStep.ActivitySelection && renderActivitySelection()}
      {currentStep === DialogStep.DateSelection && renderDateSelection()}
      {currentStep === DialogStep.SessionSelection && renderSessionSelection()}
    </Dialog>
  );
}
